/**
 * EpisodeFrame — the canonical post-timeline page layout.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ (HUD: Tabs | Logo | Globe / Profile Pic | Timeline)          │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                              │
 *   │           Picture Show                                       │
 *   │   (sticky-top, cinematic image, ~46vh)                       │
 *   │                                                              │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                              │
 *   │              Script                                          │
 *   │   (full-width, animated narration with chiseled prose)       │
 *   │                                                              │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ Vol .ı|ı.  Sound on/off          © 2026 Midnite Productions │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Apr 29 2026 — Lead's call: "Remove sidebar. Fix page to full page on
 * script." The right rail (On This Day / Bios / Monuments / Notepad /
 * Credits) is gone; the script now uses the full available width. The
 * `sidebar` prop is preserved for backwards-compat callers but is silently
 * ignored — episode pages can be migrated incrementally.
 */
import { Volume2, VolumeX } from 'lucide-react';
import { useSfxMuted } from '../lib/sfxPrefs';

export default function EpisodeFrame({
  pictureSlot,
  scriptSlot,
  // eslint-disable-next-line no-unused-vars
  sidebar = {},
  testId = 'episode-frame',
}) {
  const [muted, setMuted] = useSfxMuted();
  const soundOn = !muted;

  return (
    <div className="flex flex-col w-full" data-testid={testId}>
      <div className="flex flex-col border-t border-[#D4A84A]/20">
        {/* PICTURE SHOW — sticky-top, capped to ~half viewport so script
            scrolls underneath. Lead's Apr 28 2026 sticky-picture brief
            preserved verbatim. */}
        <div
          className="sticky top-0 z-10 w-full bg-black border-b border-[#D4A84A]/20 overflow-hidden"
          data-testid="episode-picture-show"
          style={{ position: 'sticky', height: 'min(46vh, 540px)' }}
        >
          <div className="relative w-full h-full">{pictureSlot}</div>
        </div>

        {/* SCRIPT — full page width, no sidebar. Apr 29 2026. */}
        <div
          className="relative w-full px-6 md:px-12 lg:px-20 py-8 md:py-12"
          data-testid="episode-script"
        >
          {scriptSlot}
        </div>
      </div>

      {/* FOOTER — volume/sound left, LLC right */}
      <footer
        className="flex items-center justify-between gap-4 px-4 md:px-10 py-3 border-t border-[#D4A84A]/20 bg-black"
        data-testid="episode-footer"
      >
        <div className="flex items-center gap-3 font-mono text-xs md:text-sm text-white/70">
          <button
            data-testid="episode-sound-toggle"
            onClick={() => setMuted(soundOn)}
            className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-[#2F8A92] hover:text-[#D4A84A] transition-colors"
            aria-label={soundOn ? 'Mute' : 'Unmute'}
          >
            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <span className="hidden md:inline tracking-widest uppercase text-white/55">
            Sound {soundOn ? 'on' : 'off'}
          </span>
        </div>
        <div className="font-mono text-xs md:text-sm text-white/55 tracking-wider text-right">
          © 2026 Midnite Productions LLC
        </div>
      </footer>
    </div>
  );
}
